import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, Wrench, Network, MessageCircle, FileSpreadsheet,
  Users, ShieldCheck, Upload, History, Wand2, FileText,
  ChevronRight, Mail, ListOrdered, Search, Play, ChevronDown,
} from "lucide-react";

// ---------------------------------------------------------------
// Manual — single-page user guide for Pythagore partners. Sticky
// table of contents on the left, scrollable sections on the right.
// Section ids are referenced from the ToC anchor + smooth-scrolled.
// ---------------------------------------------------------------

const TOC = [
  { id: "intro",         icon: BookOpen,        title: "Présentation" },
  { id: "register",      icon: ShieldCheck,     title: "Créer un compte" },
  { id: "bom",           icon: Wrench,          title: "Générer un BOM / BOQ" },
  { id: "networking",    icon: Network,         title: "BOM Networking · Switch & Wi-Fi" },
  { id: "renewal",       icon: History,         title: "BOM Renewal · Renouvellement" },
  { id: "manual",        icon: ListOrdered,     title: "BOM Manuel" },
  { id: "addons",        icon: Wand2,           title: "Solutions complémentaires" },
  { id: "quotes",        icon: FileSpreadsheet, title: "Mes devis · révisions" },
  { id: "review",        icon: ShieldCheck,     title: "Revue Fortinet" },
  { id: "exports",       icon: FileText,        title: "Exports PDF / Excel" },
  { id: "pricelist",     icon: Upload,          title: "Liste de prix" },
  { id: "chat",          icon: MessageCircle,   title: "Discussion directe · Salon Fortinet" },
  { id: "ai",            icon: Wand2,           title: "Assistant IA Pythagore" },
  { id: "contact",       icon: Mail,            title: "Contact & support" },
];

// Small typed components to keep the JSX flat and readable below.
const Section = ({ id, icon: Icon, title, children }) => (
  <section
    id={id}
    className="scroll-mt-20 border-b border-zinc-200 pb-10 last:border-0"
    data-testid={`manual-section-${id}`}
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="h-9 w-9 rounded-sm bg-[#D42B1E]/[0.08] text-[#D42B1E] flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4" />
      </div>
      <h2 className="text-xl font-semibold text-zinc-900 tracking-tight">{title}</h2>
    </div>
    <div className="prose prose-sm max-w-none text-zinc-700 space-y-3 leading-relaxed">
      {children}
    </div>
  </section>
);

const Step = ({ n, children }) => (
  <li className="flex gap-3">
    <span className="shrink-0 inline-flex h-6 w-6 items-center justify-center rounded-full bg-zinc-900 text-white text-[11px] font-mono">{n}</span>
    <span className="flex-1 pt-0.5">{children}</span>
  </li>
);

const Tip = ({ children }) => (
  <div className="my-2 border-l-2 border-emerald-400 bg-emerald-50/50 px-3 py-2 text-[13px] text-emerald-900">
    <strong>Astuce —</strong> {children}
  </div>
);

const Warn = ({ children }) => (
  <div className="my-2 border-l-2 border-amber-400 bg-amber-50/60 px-3 py-2 text-[13px] text-amber-900">
    <strong>Important —</strong> {children}
  </div>
);

const Kbd = ({ children }) => (
  <code className="font-mono text-[11px] px-1.5 py-0.5 bg-zinc-100 border border-zinc-200 rounded-sm text-zinc-800">{children}</code>
);

// Walkthrough accordion — lazy renders the <video> tag only when expanded so
// the page stays light on first paint. Re-collapsing pauses & rewinds the
// stream to free decoder resources.
const WalkthroughAccordion = ({ slug, title, duration }) => {
  const [open, setOpen] = useState(false);
  const videoRef = useRef(null);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (!next && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      className={`mb-4 border rounded-sm overflow-hidden transition-colors ${
        open ? "border-[#D42B1E]/40" : "border-zinc-200"
      }`}
      data-testid={`walkthrough-${slug}`}
    >
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between gap-3 px-4 py-2.5 bg-white hover:bg-zinc-50 transition-colors text-left"
        aria-expanded={open}
        data-testid={`walkthrough-toggle-${slug}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="h-7 w-7 rounded-sm bg-[#D42B1E] text-white flex items-center justify-center shrink-0">
            <Play className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-zinc-900 truncate">{title}</p>
            <p className="text-[11px] text-zinc-500">Vidéo · {duration}</p>
          </div>
        </div>
        <ChevronDown
          className={`h-4 w-4 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="bg-black" data-testid={`walkthrough-player-${slug}`}>
          <video
            ref={videoRef}
            src={`/manual/${slug}.webm`}
            controls
            autoPlay
            playsInline
            className="w-full aspect-video"
            data-testid={`walkthrough-video-${slug}`}
          />
        </div>
      )}
    </div>
  );
};

export default function Manual() {
  const [active, setActive] = useState("intro");

  // Highlight the ToC entry whose section is at the top of the viewport.
  useEffect(() => {
    const sections = TOC.map((t) => document.getElementById(t.id)).filter(Boolean);
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-zinc-50/50" data-testid="manual-page">
      {/* Hero */}
      <div className="border-b border-zinc-200 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="inline-flex items-center gap-2 border border-zinc-300 rounded-sm px-3 py-1 bg-white">
            <BookOpen className="h-3.5 w-3.5 text-[#D42B1E]" />
            <span className="text-[11px] font-semibold tracking-[0.18em] text-zinc-700 uppercase">Manuel utilisateur</span>
          </div>
          <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">
            Tout ce que vous pouvez faire avec <span className="text-[#D42B1E]">Pythagore</span>
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-zinc-600 leading-relaxed">
            Guide pas-à-pas couvrant la création d'un BOM/BOQ, la couche réseau LAN/Wi-Fi, les renouvellements,
            les exports, le chat partenaires-experts et l'assistant IA. Conçu pour qu'un nouveau partenaire soit
            opérationnel en moins de 30 minutes.
          </p>
        </div>
      </div>

      {/* Body — sticky ToC + content */}
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-12 gap-8">
        {/* ToC */}
        <aside className="lg:col-span-3" data-testid="manual-toc">
          <nav className="sticky top-20 space-y-1 border-l border-zinc-200">
            {TOC.map((t) => {
              const Icon = t.icon;
              const a = active === t.id;
              return (
                <a
                  key={t.id}
                  href={`#${t.id}`}
                  className={`group flex items-center gap-2 pl-3 pr-2 py-1.5 -ml-px border-l-2 text-[12.5px] transition-colors ${
                    a
                      ? "border-[#D42B1E] text-zinc-900 font-semibold bg-[#D42B1E]/[0.04]"
                      : "border-transparent text-zinc-600 hover:text-zinc-900 hover:border-zinc-400"
                  }`}
                  data-testid={`manual-toc-${t.id}`}
                >
                  <Icon className={`h-3.5 w-3.5 ${a ? "text-[#D42B1E]" : "text-zinc-400 group-hover:text-zinc-600"}`} />
                  <span className="flex-1 truncate">{t.title}</span>
                </a>
              );
            })}
          </nav>
        </aside>

        {/* Content */}
        <article className="lg:col-span-9 space-y-12">
          <Section id="intro" icon={BookOpen} title="Présentation">
            <p>
              Pythagore est un <strong>configurateur multi-vendor</strong> (démarrage Fortinet) qui automatise la construction
              de propositions avant-vente : sizing matériel, sélection des bundles UTP/ATP/Enterprise, ajout des add-ons
              complémentaires, exports PDF/Excel professionnels, et coordination temps réel avec les experts vendor.
            </p>
            <p>
              <strong>Trois personas</strong> partagent l'application :
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Partenaire intégrateur</strong> — construit ses BOM/BOQ et soumet pour revue.</li>
              <li><strong>Reviewer Fortinet</strong> — revoit les soumissions, ajuste les lignes et valide.</li>
              <li><strong>Administrateur Pythagore</strong> — pilote les comptes, les listes de prix et les analytics.</li>
            </ul>
            <Tip>
              Vous pouvez à tout moment relancer ce manuel depuis le menu utilisateur en haut à droite, ou via le lien
              « Manuel » du footer.
            </Tip>
          </Section>

          <Section id="register" icon={ShieldCheck} title="Créer un compte partenaire">
            <ol className="space-y-2 list-none pl-0">
              <Step n="1">Cliquez sur <Kbd>Commencer</Kbd> en haut à droite de la page d'accueil.</Step>
              <Step n="2">Renseignez vos coordonnées et votre <strong>ID PartnerPortal Fortinet</strong> ainsi que vos certifications NSE.</Step>
              <Step n="3">Un administrateur Pythagore reçoit la demande, vérifie votre profil et active votre compte (généralement en moins de 24h).</Step>
              <Step n="4">Activez la <strong>2FA TOTP</strong> dès la première connexion (Google Authenticator, 1Password, Authy…).</Step>
            </ol>
            <Warn>
              Si vous n'avez pas encore d'ID PartnerPortal, utilisez le formulaire « Demander mon accès partenaire » sur la page d'inscription —
              Pythagore préviendra l'équipe channel Fortinet pour vous aider à finaliser l'enrôlement.
            </Warn>
          </Section>

          <Section id="bom" icon={Wrench} title="Générer un BOM / BOQ">
            <WalkthroughAccordion
              slug="premier-bom"
              title="Walkthrough — Premier BOM en moins d'une minute"
              duration="≈ 30 s"
            />
            <p>
              Le générateur (page <Link to="/configurator" className="text-[#D42B1E] underline">Générateur de BOM/BOQ</Link>) propose
              quatre modes :
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>BOM Networking</strong> — déploiement complet à partir du nombre d'utilisateurs et de sites.</li>
              <li><strong>BOM Renewal</strong> — renouvellement de licences / FortiCare pour un parc existant.</li>
              <li><strong>BOM Manuel</strong> — vous choisissez les SKUs FortiGate à la main.</li>
              <li><strong>BOM Fabric Fortinet</strong> — multi-produits (en cours de développement).</li>
            </ul>
            <p>Quel que soit le mode, le flux est le même :</p>
            <ol className="space-y-2 list-none pl-0">
              <Step n="1">Remplissez le bloc <strong>Informations Client &amp; Partenaire</strong> en haut (nom client, pays, agence, intégrateur).</Step>
              <Step n="2">Choisissez le mode dans l'onglet sous le formulaire.</Step>
              <Step n="3">Indiquez nombre d'utilisateurs, sites, type de client, cas d'usage (Firewall / SD-WAN / SASE / Zero Trust / SOC).</Step>
              <Step n="4">Sélectionnez le niveau de sécurité (ATP / UTP / Enterprise) et la durée de licence (1 / 3 / 5 ans).</Step>
              <Step n="5">Cliquez sur <Kbd>Générer BOM / BOQ</Kbd>. Pythagore propose le sizing optimal + les recommandations complémentaires.</Step>
            </ol>
            <Tip>
              Vous pouvez basculer entre l'<strong>Option simple</strong> (par utilisateur) et l'<strong>Option avancée</strong>
              (sizing par Mbps / connexions concurrentes) en haut du formulaire.
            </Tip>
          </Section>

          <Section id="networking" icon={Network} title="BOM Networking · FortiSwitch & FortiAP">
            <p>
              En mode BOM Networking, un panneau dédié <strong>Couche d'accès LAN &amp; Wi-Fi</strong> s'affiche. Il pulse en
              rouge tant que vous ne l'avez pas ouvert.
            </p>
            <ol className="space-y-2 list-none pl-0">
              <Step n="1">Pythagore pré-remplit <strong>FS-148F-FPOE</strong> (48 ports PoE+) et <strong>FAP-231K</strong> (Wi-Fi 6 mid-range) avec les bonnes quantités selon votre nombre d'utilisateurs et de sites.</Step>
              <Step n="2">Ajustez le modèle via le sélecteur SKU (autocompleter en direct sur la liste de prix officielle).</Step>
              <Step n="3">Choisissez la <strong>zone réglementaire</strong> FortiAP : EMEA / APAC / Americas / Générique.</Step>
              <Step n="4">Vérifiez le voyant <strong>Cohérence physique PoE+</strong> en bas du panneau : vert si chaque AP a son port PoE+, rouge sinon (avec bouton +N switchs).</Step>
              <Step n="5">Cliquez <Kbd>Ajouter au BOM</Kbd> sur chaque carte (Switch et AP). Les <strong>FortiCare obligatoires</strong> sont automatiquement attachés.</Step>
            </ol>
            <Tip>
              Le contrôleur FortiSwitch / FortiAP est <strong>nativement embarqué</strong> dans le FortiGate (FortiLink / CAPWAP).
              Aucune licence de management additionnelle n'est requise.
            </Tip>
          </Section>

          <Section id="renewal" icon={History} title="BOM Renewal · Renouvellement">
            <p>
              Le mode Renewal calcule un devis de renouvellement à partir du <strong>parc déclaré du client</strong> :
            </p>
            <ol className="space-y-2 list-none pl-0">
              <Step n="1">Sélectionnez l'onglet <Kbd>BOM Renewal</Kbd>.</Step>
              <Step n="2">Cherchez et ajoutez chaque modèle FortiGate déjà déployé (FG-60F, FG-100F, FG-VM02…) avec sa quantité.</Step>
              <Step n="3">Choisissez le niveau FortiGuard cible (ATP / UTP / Enterprise) et la durée de renouvellement.</Step>
              <Step n="4">Pythagore résout les SKUs <Kbd>FC-10-{"{model}"}-{"{code}"}-02-{"{months}"}</Kbd> et émet le BOM consolidé.</Step>
            </ol>
          </Section>

          <Section id="manual" icon={ListOrdered} title="BOM Manuel">
            <p>
              Vous savez déjà quels FortiGate vous voulez ? Sélectionnez <strong>BOM Manuel</strong>, choisissez chaque modèle
              dans le picker, indiquez la quantité et le rôle (Siège / Agence / DC). Le moteur calcule les bundles
              FortiCare/FortiGuard exactement comme en mode automatique, mais sans sizing.
            </p>
          </Section>

          <Section id="addons" icon={Wand2} title="Solutions complémentaires & Recommandations">
            <p>
              Après chaque génération, Pythagore affiche un panneau « Solutions complémentaires » regroupant les modules
              Fortinet pertinents pour votre cas d'usage : <strong>FortiNAC, FortiAuthenticator, FortiWeb, FortiMail,
              FortiEDR, FortiSIEM, FortiSOAR</strong>, etc.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Les modules cliquables (FortiNAC, FortiAuthenticator) ouvrent un <strong>picker SKU</strong>.</li>
              <li>Les modules grisés <strong>en cours de développement</strong> ouvrent un mini-formulaire « Pré-réservation » : indiquez votre intérêt commercial (Oui / Peut-être / Non) — ce signal pilote notre roadmap.</li>
              <li>Une bannière <strong>Recommandations</strong> propose en un clic d'ajouter les add-ons reconnus comme indispensables (FortiManager, FortiAnalyzer, FortiClient EMS…).</li>
            </ul>
          </Section>

          <Section id="quotes" icon={FileSpreadsheet} title="Mes devis · révisions">
            <p>
              Chaque génération sauvegardée est consultable sur <Link to="/quotes" className="text-[#D42B1E] underline">Mes devis</Link>.
              Chaque devis est <strong>versionné</strong> et l'historique reste accessible.
            </p>
            <ol className="space-y-2 list-none pl-0">
              <Step n="1">Ouvrez un devis pour visualiser le BOM + BOQ.</Step>
              <Step n="2">Basculez en mode <strong>Édition</strong> pour ajuster les quantités, remplacer un SKU (icône crayon) ou supprimer une ligne.</Step>
              <Step n="3">Sauvegardez : Pythagore crée une nouvelle révision <Kbd>V{"{n+1}"}</Kbd> avec un delta de prix vs version précédente.</Step>
              <Step n="4">Soumettez pour <strong>revue Fortinet</strong> via le bouton <Kbd>Demander une revue</Kbd>.</Step>
            </ol>
          </Section>

          <Section id="review" icon={ShieldCheck} title="Revue Fortinet">
            <WalkthroughAccordion
              slug="soumettre-revue"
              title="Walkthrough — Soumettre un devis pour revue Fortinet"
              duration="≈ 20 s"
            />
            <p>
              Une fois soumis, votre devis arrive dans la file d'un reviewer Fortinet. Il peut :
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Modifier les lignes pour corriger un mauvais sizing ou un SKU obsolète.</li>
              <li>Ajouter une note interne visible dans le panneau Discussion.</li>
              <li>Valider le devis — il bascule alors dans <Link to="/quotes/validated" className="text-[#D42B1E] underline">Devis validés</Link> et un email récapitulatif vous est envoyé.</li>
            </ul>
          </Section>

          <Section id="exports" icon={FileText} title="Exports PDF / Excel / Schéma">
            <p>Trois exports sont disponibles depuis n'importe quel devis :</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>PDF</strong> — proposition commerciale en-tête avec logo, client final, partenaire, rep et reviewer.</li>
              <li><strong>Excel</strong> — BOM/BOQ détaillé prêt à être collé dans le système de cotation interne.</li>
              <li><strong>Schéma d'architecture (SVG)</strong> — diagramme automatique du déploiement (HQ, agences, FAZ, FMG, ZTNA…).</li>
            </ul>
            <Tip>
              Le bouton <Kbd>Envoyer par mail</Kbd> ouvre votre client par défaut avec un brouillon Outlook prêt à envoyer.
            </Tip>
          </Section>

          <Section id="pricelist" icon={Upload} title="Liste de prix officielle">
            <p>
              La page <Link to="/pricelist" className="text-[#D42B1E] underline">Liste de prix</Link> permet de :
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Charger la dernière liste Fortinet (Excel officiel) — un diff automatique compare avec l'édition précédente.</li>
              <li>Consulter les évolutions : <strong>EOS, hausses, baisses, nouveautés</strong>.</li>
              <li>Réinitialiser le moteur sur la liste seedée si une erreur d'import a corrompu vos prix.</li>
            </ul>
          </Section>

          <Section id="chat" icon={MessageCircle} title="Discussion directe · Salon Fortinet">
            <WalkthroughAccordion
              slug="discussion-directe"
              title="Walkthrough — Ouvrir une discussion sur un devis"
              duration="≈ 20 s"
            />
            <p>
              Le <strong>Salon Fortinet</strong> est un chat temps réel partagé entre partenaires et experts vendor. Pour
              ouvrir une conversation contextuelle <strong>sur un devis précis</strong> :
            </p>
            <ol className="space-y-2 list-none pl-0">
              <Step n="1">Ouvrez un devis dans <Link to="/quotes" className="text-[#D42B1E] underline">Mes devis</Link>.</Step>
              <Step n="2">Cliquez sur l'icône <MessageCircle className="inline h-3.5 w-3.5 align-text-bottom" /> en haut à droite — un panneau latéral s'ouvre.</Step>
              <Step n="3">Tapez votre question. Tous les messages restent attachés au devis et visibles par les reviewers.</Step>
            </ol>
            <Tip>
              Pour des questions hors-devis (général SD-WAN, modèle adapté à un cas d'usage…), utilisez la page
              <Link to="/contact" className="text-[#D42B1E] underline ml-1">Contact</Link> et lancez une discussion WhatsApp
              directe avec l'expert affiché sur la carte.
            </Tip>
          </Section>

          <Section id="ai" icon={Wand2} title="Assistant IA Pythagore">
            <p>
              Le bouton flottant <strong>« Py, votre assistant IA »</strong> en bas à droite ouvre un copilote conversationnel
              capable de :
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Justifier une recommandation (« pourquoi me proposes-tu FortiManager pour ce devis ? »)</li>
              <li>Comparer deux SKUs FortiGate (« FG-100F vs FG-200F »)</li>
              <li>Expliquer un bundle ou la différence ATP / UTP / Enterprise.</li>
              <li>Aider à rédiger un email commercial à partir du devis courant.</li>
            </ul>
            <Warn>
              L'assistant a accès à votre devis courant pour fournir des réponses contextuelles, mais ne stocke pas vos
              conversations à des fins d'entraînement. Voir la politique de confidentialité.
            </Warn>
          </Section>

          <Section id="contact" icon={Mail} title="Contact &amp; support">
            <p>Trois canaux selon votre besoin :</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Question commerciale ou avant-vente</strong> — page <Link to="/contact" className="text-[#D42B1E] underline">Contact</Link>, clic sur l'icône WhatsApp de l'expert pertinent.</li>
              <li><strong>Question technique sur un devis</strong> — Discussion directe dans le devis (voir section précédente).</li>
              <li><strong>Bug / suggestion produit</strong> — écrivez à <a className="text-[#D42B1E] underline" href="mailto:support@pythagore.biz">support@pythagore.biz</a>.</li>
            </ul>

            <div className="mt-6 inline-flex items-center gap-2 text-sm text-zinc-600">
              <Search className="h-4 w-4" />
              <span>Vous ne trouvez pas ce que vous cherchez ?</span>
              <Link to="/contact" className="text-[#D42B1E] underline inline-flex items-center gap-1">
                Contactez un expert <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
          </Section>

          {/* Footer mini-CTA */}
          <div className="border border-zinc-200 rounded-sm bg-white p-5 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-sm bg-zinc-900 text-white flex items-center justify-center">
                <Users className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-semibold text-zinc-900">Prêt à monter votre premier BOM ?</p>
                <p className="text-xs text-zinc-500">Pythagore vous accompagne du sizing à l'export final.</p>
              </div>
            </div>
            <Link
              to="/configurator"
              className="inline-flex items-center gap-2 bg-[#D42B1E] hover:bg-[#B32218] text-white text-sm font-medium px-4 py-2 rounded-sm transition-colors"
              data-testid="manual-cta-configurator"
            >
              Ouvrir le configurateur
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
}
